import React, { useEffect } from 'react';
import JustificationContainer from '~/components/atom/JustificationContainer';
import type { LimitFragment } from '~/graphql/types';
import getPercentage from '~/util/getPercentage';
import UpgradePlanCta from '../UpgradePlanCTA';
import StrapiLink from '../StrapiLink';
import PercentageBar from '~/components/molecule/PercentageBar';
import type { SystemSize } from '~/theme';
import type { UpgradeSourceType } from './constants';
import useTrackEvent from '~/hooks/useTrackEvent';

const text = {
  header: 'Let op!',
  strapiLinkLabel: 'Leer meer over dit limiet',
};

export type Props = {
  dataTestId?: string;
  className?: string;
  textObj: {
    body: {
      approachingLimit: string;
      overLimit: string;
    };
    buttonLabel: string;
  };
  margin?: Array<SystemSize | null>;
  /**
   * The source that influenced the user to upgrade (e.g., 'contacts', 'forms', 'automations').
   * Used for analytics tracking.
   */
  upgradeSource?: UpgradeSourceType;
  /**
   * The limit to check.
   */
  limit: LimitFragment;
  /**
   * The current value of the limit.
   */
  currentValue: number;
  /**
   * The percentage at which the CTA should be shown.
   * @default 80
   */
  thresholdPercentage?: number;
};

const LimitUpgradeCTA: React.FCC<Props> = ({
  dataTestId,
  textObj,
  limit,
  currentValue,
  upgradeSource,
  margin = ['base', null, null, null],
  thresholdPercentage = 80,
}) => {
  const trackEvent = useTrackEvent();
  const limitPercentage = getPercentage(currentValue, limit.limit);
  const approachingLimit = limitPercentage < 100;
  const overLimit = limitPercentage > 100;

  useEffect(() => {
    if (limit && overLimit) {
      /** ----- TRACKING: limit_reached ----- */
      trackEvent({
        eventName: 'limit_reached',
        parameters: {
          limit_type: limit.id,
          current_value: currentValue,
          limit_value: limit.limit,
          limit_percentage: limitPercentage,
          is_over_limit: overLimit,
          upgrade_source: upgradeSource,
        },
      });
    }
  }, [
    limit,
    limitPercentage,
    thresholdPercentage,
    currentValue,
    overLimit,
    upgradeSource,
    trackEvent,
  ]);

  if (!(limit && limitPercentage >= thresholdPercentage)) return null;

  return (
    <UpgradePlanCta
      dataTestId={dataTestId}
      header={text.header}
      description={
        approachingLimit
          ? textObj.body.approachingLimit
          : textObj.body.overLimit
      }
      buttonLabel={textObj.buttonLabel}
      upgradeSource={upgradeSource}
      margin={margin}
    >
      <JustificationContainer margin={['xs', null, null, null]}>
        <StrapiLink
          label={text.strapiLinkLabel}
          reason={limit.reason}
          color="white"
        />
      </JustificationContainer>
      <JustificationContainer margin={['base', null, null, null]} width="100%">
        <PercentageBar
          currentValue={currentValue}
          total={limit.limit}
          color={overLimit ? 'danger' : 'white'}
          visiblePercentage
        />
      </JustificationContainer>
    </UpgradePlanCta>
  );
};

export default LimitUpgradeCTA;
